import React, { useEffect, useState, useCallback } from "react";
import { Button, Spinner, Table, Modal } from "react-bootstrap";
import OmrService from "../../../_services/OmrService";

interface Props {
  omrTestId: number;
  testName: string;
  sectionsConfig: Array<{ name: string }>;
}

const ResultsTab: React.FC<Props> = ({ omrTestId, testName, sectionsConfig }) => {
  const [results, setResults] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const loadPreview = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await OmrService.getResultsPreview(omrTestId);
      setResults(resp.data?.data?.results || []);
      setSummary(resp.data?.data?.summary || null);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [omrTestId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      setShowConfirm(false);
      const resp = await OmrService.finalize(omrTestId);
      const data = resp.data?.data;
      setMessage(`Finalized: ${data?.saved || 0} student(s) saved, ${data?.skipped || 0} skipped`);
      loadPreview();
    } catch (err: any) {
      setMessage("Finalize failed: " + (err.message || ""));
    } finally {
      setFinalizing(false);
    }
  };

  const handleExport = async () => {
    try {
      const resp = await OmrService.exportExcel(omrTestId);
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OMR_Results_${testName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage("Export failed");
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div>
      {message && (
        <div style={{
          padding: "6px 12px", borderRadius: 4, marginBottom: 12,
          background: message.includes("failed") ? "#f8d7da" : "#d4edda",
          color: message.includes("failed") ? "#721c24" : "#155724",
        }}>
          {message}
        </div>
      )}

      {/* Summary bar */}
      {summary && (
        <div style={{ fontSize: "0.85rem", marginBottom: 12, display: "flex", gap: 16 }}>
          <span>Total: <strong>{summary.totalScans}</strong></span>
          <span>Processed: <strong>{summary.processed}</strong></span>
          <span style={{ color: summary.errored > 0 ? "#dc3545" : undefined }}>Errors: <strong>{summary.errored}</strong></span>
          <span style={{ color: summary.needsReview > 0 ? "#ffc107" : undefined }}>Needs review: <strong>{summary.needsReview}</strong></span>
          <span style={{ color: summary.unmatched > 0 ? "#dc3545" : undefined }}>Unmatched: <strong>{summary.unmatched}</strong></span>
        </div>
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <Button variant="primary" size="sm" onClick={() => setShowConfirm(true)} disabled={finalizing || results.length === 0}>
          {finalizing ? <><Spinner animation="border" size="sm" /> Finalizing...</> : "Finalize & Save to Test System"}
        </Button>
        <Button variant="secondary" size="sm" onClick={handleExport} disabled={results.length === 0}>
          Export Excel
        </Button>
      </div>

      {results.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: "var(--text-faint)" }}>No processed results. Go to "Process & Review" tab first.</div>
      ) : (
        <Table bordered hover size="sm" style={{ fontSize: "0.8rem" }}>
          <thead>
            <tr style={{ background: "var(--surface-page)" }}>
              <th>Rank</th>
              <th>Roll No</th>
              <th>Student Name</th>
              <th>Set</th>
              {sectionsConfig.map((s) => <th key={s.name}>{s.name}</th>)}
              <th>Total</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r: any) => (
              <tr key={r.scanId}>
                <td style={{ fontWeight: "bold" }}>{r.rank}</td>
                <td>{r.rollNumber || "—"}</td>
                <td>
                  {r.studentName || <span style={{ color: "var(--status-danger)", fontSize: "0.75rem" }}>Unmatched</span>}
                </td>
                <td>{r.setCode || "—"}</td>
                {sectionsConfig.map((s) => {
                  const sec = r.sections?.find((rs: any) => rs.name === s.name);
                  return <td key={s.name}>{sec ? `${sec.marks}/${sec.total}` : "—"}</td>;
                })}
                <td style={{ fontWeight: "bold" }}>{r.totalMarks}/{r.maxMarks}</td>
                <td>{r.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Confirm Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton><Modal.Title>Confirm Finalize</Modal.Title></Modal.Header>
        <Modal.Body>
          This will save {results.filter((r: any) => r.studentName).length} student results into the test system.
          Students without a matched name will be skipped. This action can be run again (existing entries are skipped).
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleFinalize}>Finalize</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResultsTab;
